import { ReactNode, useRef, useState, useEffect } from 'react';
import './scrollableContainer.scss';
import Icon, { IconType } from '../icon/Icon';
import classNames from 'classnames';

interface ScrollableContainerProps {
  children: ReactNode;
  className?: string;
  scrollStep?: number;
}

const ScrollableContainer = ({
  children,
  className = '',
  scrollStep = 200,
}: ScrollableContainerProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollability = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 to account for rounding errors
  };

  useEffect(() => {
    checkScrollability();

    // Check on window resize
    window.addEventListener('resize', checkScrollability);

    // Initial check after content might have loaded/rendered
    const timeoutId = setTimeout(checkScrollability, 100);

    return () => {
      window.removeEventListener('resize', checkScrollability);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleScroll = () => {
    checkScrollability();
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollStep,
        behavior: 'smooth',
      });
      setTimeout(checkScrollability, 500); // Check again after animation
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollStep,
        behavior: 'smooth',
      });
      setTimeout(checkScrollability, 500); // Check again after animation
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement === scrollContainerRef.current) {
        if (e.key === 'ArrowRight' && canScrollRight) {
          scrollRight();
        } else if (e.key === 'ArrowLeft' && canScrollLeft) {
          scrollLeft();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canScrollLeft, canScrollRight, scrollStep]);

  return (
    <div className={`scrollable-container-wrapper ${className}`}>
      {canScrollLeft && (
        <button
          type="button"
          className="scrollable-container__nav scrollable-container__nav--left"
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <Icon iconType={IconType.ArrowLeft} className="u-icon-tiny" />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className={classNames('scrollable-container', {
          'at-start': !canScrollLeft,
          'at-end': !canScrollRight,
        })}
        onScroll={handleScroll}
        tabIndex={0}
        role="region"
        aria-label="Scrollable content"
      >
        {children}
      </div>

      {canScrollRight && (
        <button
          type="button"
          className="scrollable-container__nav scrollable-container__nav--right"
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <Icon iconType={IconType.ArrowRight} className="u-icon-tiny" />
        </button>
      )}
    </div>
  );
};

export default ScrollableContainer;
